/** @jsxImportSource @emotion/react */
import { DesignSystemProvider } from '@quickbit/qb-design-system';
import { Fragment, useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import { Routes, Route, Outlet } from 'react-router-dom';
import { useAsync } from 'react-use';
import { getMicroArticles } from 'api/contentful/getMicroArticles';
import { Layout } from 'components';
import { OrderIdProvider, OrderProvider } from 'context';
import copyContentJson from 'copy.json';
import { useLocale } from 'hooks';
import {
  Home,
  Success,
  Cancelled,
  NotFound,
  ProcessingInternal,
  ProcessingExternal,
  Docs,
  InvalidAmount,
  Refunding,
  Refunded,
  Refund,
} from 'pages';
import { SignUp } from 'pages/SignUp';
import { BusinessInformation, GeneralInformation } from 'pages/SignUp/steps/';
import { Locale } from 'types';
import { renderText } from 'utils/renderText';

const App = () => {
  const { locale } = useLocale();

  const content = useMemo(() => {
    const copyContent = copyContentJson as Record<
      string,
      Record<Locale, string>
    >;
    return Object.keys(copyContent).reduce(
      (prev, currentKey) => ({
        ...prev,
        [currentKey]: copyContent[currentKey][locale],
      }),
      {}
    );
  }, [locale]);

  const { value } = useAsync(async () => getMicroArticles('en-US'), [locale]);

  return (
    <IntlProvider
      locale={locale}
      messages={{ ...content, ...(value ? value : {}) }}
      textComponent={({ children }: { children?: string[] }) => {
        if (!children) return null;
        return (
          <Fragment>
            {renderText(Array.isArray(children) ? children[0] : children)}
          </Fragment>
        );
      }}
    >
      <DesignSystemProvider>
        <Routes>
          <Route key="docs" path="/docs/*" element={<Docs />} />
          <Route key="sign-up" path="/sign-up/*" element={<SignUp basePath='/sign-up' />}>
            <Route
              key="general-information"
              path="general-information"
              element={<GeneralInformation />}
            />
            <Route
              key="business-information"
              path="business-information"
              element={<BusinessInformation showTerms={false} />}
            />
          </Route>
          <Route key="new-sign-up" path="/new-sign-up/*" element={<SignUp basePath='/new-sign-up' />}>
            <Route
              key="general-information"
              path="general-information"
              element={<GeneralInformation />}
            />
            <Route
              key="business-information"
              path="business-information"
              element={<BusinessInformation showTerms={true} />}
            />
          </Route>
          <Route key="not-found" path="/not-found" element={<NotFound />} />
          <Route
            key="/"
            path="/"
            element={
              <OrderIdProvider>
                <OrderProvider>
                  <Layout>
                    <Outlet />
                  </Layout>
                </OrderProvider>
              </OrderIdProvider>
            }
          >
            <Route key="index" index element={<Home />} />
            <Route
              key="processing-internal"
              path="/processing-internal"
              element={<ProcessingInternal />}
            />
            <Route
              key="processing-external"
              path="/processing-external"
              element={<ProcessingExternal />}
            />
            <Route key="success" path="/success" element={<Success />} />
            <Route
              key="invalid"
              path="/invalid-amount"
              element={<InvalidAmount />}
            />
            <Route key="refund" path="/refund" element={<Refund />} />
            <Route key="refunding" path="/refunding" element={<Refunding />} />
            <Route key="refunded" path="/refunded" element={<Refunded />} />
            <Route key="cancelled" path="/cancelled" element={<Cancelled />} />
          </Route>

          {/* catch all non existing routes */}
          <Route key="rest" path="*" element={<NotFound />} />
        </Routes>
      </DesignSystemProvider>
    </IntlProvider>
  );
};

export default App;
